import { ChallengeLabStatus, deriveChallengeLabStatus, JamProgress } from '@/src/types/JamChallengeDetails';
import { IJamChallenge } from '@/src/types/JamChallenges';
import { noop } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseChallengeProgressParams {
  /** The event's challenges (list data). */
  challenges: IJamChallenge[] | undefined;
  /** Per-challenge refresh counter from the context. A changed tick triggers a `/progress` fetch. */
  challengeRefreshTicks: { [challengeId: string]: number } | undefined;
  /** Once the event ends no status can change, so fetching stops. */
  eventEnded: boolean;
  /** Fetches a single challenge's authoritative progress. Caller binds it to the current event. */
  fetchProgress: (challengeId: string) => Promise<JamProgress>;
}

/** Map `JamProgress` (full, lab-aware) to a derivation input. */
const progressToInput = (p: JamProgress, solved?: boolean) => ({
  started: p.started,
  completed: p.completed,
  solved,
  hasAwsResources: p.hasAwsResources,
  hasExternalResources: p.hasExternalResources,
  awaitingLabAssignment: p.awaitingLabAssignment,
  anyTaskCompleted: p.taskProgress?.some((task) => task.completed),
});

/** Map list data (`IJamChallenge`, no lab fields) to a derivation input. Can yield SOLVED /
 * NOT_STARTED reliably, but never PREPARING (it lacks the lab-backed fields). */
const listDataToInput = (c: IJamChallenge) => ({
  started: c.started,
  completed: c.completed,
  solved: c.solved,
  anyTaskCompleted: c.taskProgress?.some((task) => task.completed),
});

/**
 * Per-challenge `/progress` and lab status for every challenge in the event. Websocket messages
 * only bump `challengeRefreshTicks`; each changed tick fetches `/progress`, plus a one-time backfill
 * on load. No polling, so a dropped websocket leaves state stale until reload.
 */
export const useChallengeProgress = ({
  challenges,
  challengeRefreshTicks,
  eventEnded,
  fetchProgress,
}: UseChallengeProgressParams): {
  statuses: { [challengeId: string]: ChallengeLabStatus };
  progressById: { [challengeId: string]: JamProgress };
} => {
  const [statuses, setStatuses] = useState<{ [challengeId: string]: ChallengeLabStatus }>({});
  const [progressById, setProgressById] = useState<{ [challengeId: string]: JamProgress }>({});

  const setStatus = useCallback((challengeId: string, next: ChallengeLabStatus) => {
    setStatuses((prev) => (prev[challengeId] === next ? prev : { ...prev, [challengeId]: next }));
  }, []);

  // Apply an authoritative `/progress` response: store it (for task-nav reuse) and derive status.
  const applyProgress = useCallback(
    (challengeId: string, progress: JamProgress, solved?: boolean) => {
      setProgressById((prev) => (prev[challengeId] === progress ? prev : { ...prev, [challengeId]: progress }));
      setStatus(challengeId, deriveChallengeLabStatus(progressToInput(progress, solved)));
    },
    [setStatus]
  );

  // Latest challenges readable from async callbacks without being a dependency that restarts loops.
  const challengesRef = useRef(challenges);
  challengesRef.current = challenges;

  // Per-challenge guards for the trigger and backfill effects
  const lastSeenTick = useRef<{ [challengeId: string]: number }>({});
  const backfilled = useRef<{ [challengeId: string]: boolean }>({});

  // Responses can resolve out of order, so only the latest request per challenge may apply.
  // Ids are globally unique so clearing `latestRequest` on event switch also drops old-event responses.
  const requestCounter = useRef(0);
  const latestRequest = useRef<{ [challengeId: string]: number }>({});

  const fetchAndApply = useCallback(
    (challengeId: string) => {
      requestCounter.current += 1;
      const requestId = requestCounter.current;
      latestRequest.current[challengeId] = requestId;
      void fetchProgress(challengeId)
        .then((progress) => {
          if (latestRequest.current[challengeId] !== requestId) {
            return;
          }
          const solved = challengesRef.current?.find((ch) => ch.id === challengeId)?.solved;
          applyProgress(challengeId, progress, solved);
        })
        // A failed fetch keeps the last known state until the next tick.
        .catch(noop);
    },
    [fetchProgress, applyProgress]
  );

  const ticksRef = useRef(challengeRefreshTicks);
  ticksRef.current = challengeRefreshTicks;
  const previousFetchProgress = useRef<typeof fetchProgress>();
  // The previous event's challenge list, still in props until the new event's list loads.
  const staleChallenges = useRef<IJamChallenge[] | undefined>();
  // List from the previous commit (updated by the last effect below), so a switch that arrives
  // together with the new list doesn't mark the new list stale.
  const committedChallenges = useRef(challenges);

  // A new `fetchProgress` means a new event. Ticks accumulated for the old event are marked seen
  // (not cleared), else every one would refetch an old challenge id against the new event.
  useEffect(() => {
    const isEventSwitch = !!previousFetchProgress.current && previousFetchProgress.current !== fetchProgress;
    previousFetchProgress.current = fetchProgress;
    if (!isEventSwitch) {
      return;
    }
    setStatuses({});
    setProgressById({});
    backfilled.current = {};
    lastSeenTick.current = { ...ticksRef.current };
    latestRequest.current = {};
    staleChallenges.current = committedChallenges.current;
  }, [fetchProgress]);

  // Read inside effects (they run after the reset above): the previous event's list is ignored so its
  // ids aren't seeded or backfilled against the new event.
  const currentEventChallenges = () => (challenges === staleChallenges.current ? undefined : challenges);

  // Seed from list data. A started challenge's lab-backed status can't be told from list data, so
  // it resolves via the backfill / trigger fetches. NOT_STARTED only seeds an empty slot: list data
  // lags, so overwriting would clobber a fetched PREPARING/IN_PROGRESS back to NOT_STARTED until the
  // next fetch. SOLVED is terminal, so it always wins.
  useEffect(() => {
    currentEventChallenges()?.forEach((challenge) => {
      const derived = deriveChallengeLabStatus(listDataToInput(challenge));
      if (derived === ChallengeLabStatus.SOLVED) {
        setStatus(challenge.id, derived);
      } else if (derived === ChallengeLabStatus.NOT_STARTED) {
        setStatuses((prev) => (prev[challenge.id] === undefined ? { ...prev, [challenge.id]: derived } : prev));
      }
    });
  }, [challenges, setStatus]);

  // Trigger-driven refresh: when a challenge's refresh tick changes, fetch authoritative progress.
  useEffect(() => {
    if (eventEnded) {
      return;
    }
    const ticks = challengeRefreshTicks ?? {};
    Object.keys(ticks).forEach((challengeId) => {
      if (lastSeenTick.current[challengeId] !== ticks[challengeId]) {
        lastSeenTick.current[challengeId] = ticks[challengeId];
        fetchAndApply(challengeId);
      }
    });
  }, [challengeRefreshTicks, eventEnded, fetchAndApply]);

  // One-time backfill: fetch `/progress` once per started (not-solved) challenge and per challenge
  // missing list task data, so fresh-load state is correct for challenges already in flight.
  useEffect(() => {
    if (eventEnded) {
      return;
    }
    (currentEventChallenges() ?? []).forEach((challenge) => {
      const needsBackfill = (!!challenge.started && !challenge.solved) || !challenge.taskProgress?.length;
      if (needsBackfill && !backfilled.current[challenge.id]) {
        backfilled.current[challenge.id] = true;
        fetchAndApply(challenge.id);
      }
    });
  }, [challenges, eventEnded, fetchAndApply]);

  useEffect(() => {
    committedChallenges.current = challenges;
  }, [challenges]);

  return { statuses, progressById };
};
